<template>
    <div class="row g-3 mb-4">
        <!-- NAGŁÓWEK SEKCJI -->
        <div ref="headerRef"
            class="col-12 d-flex justify-content-between align-items-center border-bottom pb-2 cursor-pointer"
            @click="toggleSection">
            <h6 class="fw-bold mb-0">2. Dane pojazdu</h6>
            <button type="button" class="btn btn-sm btn-link text-decoration-none p-0">
                <i class="bi" :class="sectionCollapse ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </button>
        </div>

        <!-- TREŚĆ SEKCJI -->
        <div class="col-12" v-show="sectionCollapse">
            <div class="row g-3">
                <div class="col-md-3">
                    <label class="form-label fw-semibold">Marka</label>
                    <input type="text" class="form-control" v-model="vehicle.make" placeholder="np. Audi" />
                </div>
                <div class="col-md-3">
                    <label class="form-label fw-semibold">Model</label>
                    <input type="text" class="form-control" v-model="vehicle.model" placeholder="np. A4" />
                </div>
                <div class="col-md-3">
                    <label class="form-label fw-semibold">Pojemność silnika (cm³)</label>
                    <input type="text" class="form-control" v-model="vehicle.engineCapacity" placeholder="np. 1968" />
                </div>
                <div class="col-md-3 d-flex align-items-end">
                    <button type="button"
                        class="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-1"
                        @click="$emit('openVehicleSelector')">
                        <i class="bi bi-car-front-fill"></i>
                        <span>Moje pojazdy</span>
                    </button>
                </div>

                <!-- RZĄD 2 -->
                <div class="col-md-3">
                    <label class="form-label fw-semibold">Skrzynia biegów</label>
                    <input type="text" class="form-control" v-model="vehicle.gearbox" placeholder="np. Manualna" />
                </div>
                <div class="col-md-3">
                    <label class="form-label fw-semibold">Typ paliwa</label>
                    <input type="text" class="form-control" v-model="vehicle.fuelType" placeholder="np. Diesel" />
                </div>
                <div class="col-md-3">
                    <label class="form-label fw-semibold">Moc (KM)</label>
                    <input type="text" class="form-control" v-model="vehicle.powerHp" placeholder="np. 150" />
                </div>
                <div class="col-md-3">
                    <label class="form-label fw-semibold">Kolor</label>
                    <input type="text" class="form-control" v-model="vehicle.color" placeholder="np. Czarny metallik" />
                </div>

                <!-- RZĄD 3 -->
                <div class="col-md-3">
                    <label class="form-label fw-semibold">Stan licznika (km)</label>
                    <input type="text" class="form-control" v-model="vehicle.mileage" placeholder="np. 185000" />
                </div>
                <div class="col-md-3">
                    <label class="form-label fw-semibold">Nr rejestracyjny</label>
                    <input type="text" class="form-control" v-model="vehicle.registrationPlate"
                        placeholder="np. WI 12345" />
                </div>
                <div class="col-md-3">
                    <label class="form-label fw-semibold">Rok produkcji</label>
                    <input type="number" class="form-control" v-model="vehicle.productionYear" placeholder="np. 2018"
                        min="1900" max="2099" />
                </div>
                <div class="col-md-3">
                    <label class="form-label fw-semibold">Data 1. rejestracji</label>
                    <input type="date" class="form-control" v-model="vehicle.firstRegistrationDate" />
                </div>
                <div class="col-md-3">
                    <label class="form-label fw-semibold">Typ pojazdu</label>
                    <select class="form-select form-select-sm" v-model="vehicle.type">
                        <option value="osobowy">Osobowy</option>
                        <option value="dostawczy">Dostawczy</option>
                        <option value="ciezarowy">Ciężarowy</option>
                    </select>
                </div>
                <!-- RZĄD 4 -->
                <div class="col-9">
                    <label class="form-label fw-semibold">VIN</label>
                    <input type="text" class="form-control font-monospace" v-model="vehicle.vin"
                        placeholder="np. WAUZZZ8K9JA000000" />
                </div>

                <!-- RZĄD 5 -->
                <div class="col-12">
                    <label class="form-label fw-semibold">Pozostałe dane</label>
                    <textarea class="form-control" rows="4" v-model="vehicle.additionalInfo"
                        placeholder="Uwagi do pojazdu, stan techniczny, uszkodzenia, wyposażenie..."></textarea>
                </div>
            </div>

            <!-- SEKCJA DODATKOWYCH BOXÓW (GWARANCJA / OC) -->
            <div v-if="vehicleAddons && vehicleAddons.length > 0" class="mt-4 pt-3 border-top">
                <label class="fw-semibold mb-2 d-block">Sugerowane pakiety i opcje dodatkowe dla pojazdu:</label>
                <div class="row g-3">
                    <div v-for="addon in vehicleAddons" :key="addon.id" class="col-md-3">
                        <div class="card h-100 border text-center p-3 shadow-sm bg-light">
                            <div class="fw-bold text-primary mb-1">{{ addon.title }}</div>
                            <div class="fs-5 fw-semibold mb-2">{{ addon.priceNet }} PLN netto</div>
                            <button type="button" class="btn btn-sm btn-outline-success mt-auto"
                                @click="$emit('addAddon', addon)">
                                <i class="bi bi-cart-plus me-1"></i>Dodaj do faktury
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'

const props = defineProps({
    modelValue: {
        type: Object,
        required: true
    },
    vehicleAddons: {
        type: Array,
        default: () => []
    }
})

const emit = defineEmits(['update:modelValue', 'openVehicleSelector', 'addAddon'])

// Reaktywne wiązanie obiektu vehicle
const vehicle = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val)
})

const sectionCollapse = ref(false)
const headerRef = ref(null)

const toggleSection = async () => {
    sectionCollapse.value = !sectionCollapse.value

    if (sectionCollapse.value) {
        await nextTick()
        headerRef.value?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        })
    }
}
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
    user-select: none;
}

.font-monospace {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
</style>